import { useLingui } from '@lingui/react/macro';
import { X } from 'lucide-react';
import { USER_DEFAULT_AVATAR } from 'shared/constants/links';
import { FileSize } from 'ud-form/components/file-size';
import { SmallImageHint } from 'ud-form/components/image-upscale/small-image-hint';
import { SmallImageHintProps } from 'ud-form/components/image-upscale/types';
import UDText from 'ud-ui/components/text';
import { DropzonePreview } from '../domain/previews';
import { FileName, PreviewCard, REMOVE_BUTTON_CLASS, RemoveButton, Thumb } from './styles';

export interface PreviewItemProps {
  preview: DropzonePreview;
  size: number;
  showFileName?: boolean;
  onRemove?: () => void;
  onOpen?: () => void;
  /** Картинка мелкая — под миниатюрой кнопка «Улучшить». */
  upscaleHint?: SmallImageHintProps | null;
}

/**
 * Одна выбранная картинка: миниатюра, её имя и вес.
 *
 * Вес показывается только у файла, выбранного сейчас, — у картинки, которая уже
 * лежит на бэкенде, в поле один адрес, и взять вес неоткуда. Решает это сам
 * `FileSize`, поэтому здесь никаких условий нет.
 */
export function PreviewItem({ preview, size, showFileName, onRemove, onOpen, upscaleHint }: PreviewItemProps) {
  const { t } = useLingui();
  const fileName = preview.name;

  return (
    <PreviewCard size={size}>
      {onRemove && (
        <RemoveButton
          type="button"
          className={REMOVE_BUTTON_CLASS}
          aria-label={fileName ? t`Удалить изображение ${fileName}` : t`Удалить изображение`}
          onClick={(event) => {
            event.stopPropagation();
            onRemove();
          }}
        >
          <X size={16} />
        </RemoveButton>
      )}

      <Thumb
        size={size}
        onClick={(event) => {
          // Превью под зоной живёт вне кликабельной области, а внутри зоны —
          // внутри неё: без остановки клик открыл бы ещё и выбор файла.
          event.stopPropagation();
          onOpen?.();
        }}
        style={{ cursor: onOpen ? 'pointer' : 'default' }}
      >
        <img src={preview.url ?? USER_DEFAULT_AVATAR} alt={preview.name ?? t`Изображение`} />
      </Thumb>

      {showFileName && preview.name && (
        <UDText type="subhead" style={{ width: '100%', minWidth: 0 }}>
          {/* Полное имя остаётся доступным подсказкой браузера — обрезка прячет его целиком. */}
          <FileName title={preview.name}>{preview.name}</FileName>
        </UDText>
      )}

      <FileSize value={preview.size} />

      {upscaleHint && (
        // Кнопка живёт внутри перетаскиваемой карточки, а бывает и внутри самой зоны: без
        // остановки нажатие начало бы перетаскивание (сенсоры dnd-kit слушают предка) или
        // открыло выбор файла.
        <div
          onClick={(event) => event.stopPropagation()}
          onPointerDown={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
          role="presentation"
        >
          <SmallImageHint {...upscaleHint} compact />
        </div>
      )}
    </PreviewCard>
  );
}
